import { useEffect, useMemo, useState } from 'react'

import { add, isAfter, isSameSecond } from 'date-fns'

/**
 * Constants for date/time validation
 */
const MIN_DURATION_MINUTES = 1

/**
 * Combines date and time into a single Date object
 */
const combineDateTime = (date: Date, time: Date): Date => {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    time.getHours(),
    time.getMinutes(),
    0,
    0
  )
}

/**
 * Props for useDateTimeValidation hook
 */
export interface UseDateTimeValidationIProps {
  startDate: Date
  startTime: Date
  initialEndDate: Date
  initialEndTime: Date
  isEnabled: boolean
}

/**
 * Return type for useDateTimeValidation hook
 */
export interface UseDateTimeValidationReturnIProps {
  endDate: Date
  endTime: Date
  setEndDate: (date: Date) => void
  setEndTime: (time: Date) => void
}

/**
 * Hook that validates date/time inputs and auto-corrects end date/time if needed.
 * Ensures that the end date/time is always after the start date/time by at least
 * MIN_DURATION_MINUTES when validation is enabled.
 */
export const useDateTimeValidation = ({
  startDate,
  startTime,
  initialEndDate,
  initialEndTime,
  isEnabled,
}: UseDateTimeValidationIProps): UseDateTimeValidationReturnIProps => {
  const [endDate, setEndDate] = useState<Date>(initialEndDate)
  const [endTime, setEndTime] = useState<Date>(initialEndTime)

  // Sync with initial values when they change
  useEffect(() => {
    setEndDate(initialEndDate)
    setEndTime(initialEndTime)
  }, [initialEndDate, initialEndTime])

  // Calculate valid values based on current state
  const validatedDates = useMemo(() => {
    if (!isEnabled) {
      return { validEndDate: endDate, validEndTime: endTime }
    }

    const startDateTime = combineDateTime(startDate, startTime)
    const endDateTime = combineDateTime(endDate, endTime)

    if (
      !isAfter(endDateTime, startDateTime) ||
      isSameSecond(endDateTime, startDateTime)
    ) {
      const newEndDateTime = add(startDateTime, {
        minutes: MIN_DURATION_MINUTES,
      })
      return {
        validEndDate: newEndDateTime,
        validEndTime: newEndDateTime,
      }
    }

    return { validEndDate: endDate, validEndTime: endTime }
  }, [startDate, startTime, endDate, endTime, isEnabled])

  // Auto-correct end date/time only when start date/time changes
  useEffect(() => {
    if (!isEnabled) return

    const { validEndDate, validEndTime } = validatedDates

    if (
      validEndDate.getTime() !== endDate.getTime() ||
      validEndTime.getTime() !== endTime.getTime()
    ) {
      setEndDate(validEndDate)
      setEndTime(validEndTime)
    }
  }, [startDate, startTime, isEnabled, validatedDates, endDate, endTime])

  return {
    endDate: validatedDates.validEndDate,
    endTime: validatedDates.validEndTime,
    setEndDate,
    setEndTime,
  }
}
